import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// Import the hybrid communication manager
import HybridCommunicationManager from './services/HybridCommunicationManager';
import Leaderboard from './components/Leaderboard';

// Import extracted UI helpers
import {
  getPlayersWithChips,
  getCurrentPlayer,
  canPlayerCheck,
  calculateCallAmount
} from './GameUIHelpers';

// Import stats and notifications helpers
import {
  useNotifications,
  NotificationsContainer
} from './GameStatsHelpers';

// Import component modules
import {
  JoinRoomModal,
  ReconnectModal,
  ReconnectionOverlay
} from './components/Modals';

import {
  MobileLobby,
  MobileTable,
  DesktopLobby
} from './components/Layouts';

import {
  PlayersList,
  GameTable,
  GameHeader,
  ShowdownResults
} from './components/GameComponents';

function AppFixed() {
  const [roomId, setRoomId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [errorMessage, setErrorMessage] = useState('');
  const [players, setPlayers] = useState([]);
  const [myPlayerId, setMyPlayerId] = useState('');
  const myPlayerIdRef = useRef(''); // Add ref to avoid stale closure
  const [gameInProgress, setGameInProgress] = useState(false);
  const [gameState, setGameState] = useState(null);
  const [holeCards, setHoleCards] = useState([]);
  
  // Use extracted notifications hook
  const { notifications, addNotification, removeNotification } = useNotifications();
  
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [lastGameSession, setLastGameSession] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('lobby'); // 'lobby', 'table'
  const [isMobile, setIsMobile] = useState(false);
  const [mobileTableView, setMobileTableView] = useState('table'); // 'table', 'stats'
  const [timeoutWarnings, setTimeoutWarnings] = useState(new Set()); // Player IDs with timeout warnings
  const [showdownResults, setShowdownResults] = useState(null); // Winner/showdown information
  const showdownResultsRef = useRef(null); // Ref to track showdown state for closures
  const hybridClient = useRef(null);
  const [p2pStatus, setP2pStatus] = useState({
    websocket: 'disconnected',
    p2p: 'disconnected',
    signaling: 'disconnected',
    connectedPeers: 0,
    fallbackMode: false
  });
  const [p2pGameMode, setP2pGameMode] = useState(false);
  const [p2pGameStats, setP2pGameStats] = useState(null);
  const [mentalPokerPhase, setMentalPokerPhase] = useState('idle');
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  // Debug leaderboard state changes and player data
  useEffect(() => {
    console.log('🔄 LEADERBOARD STATE CHANGED:', { 
      showLeaderboard, 
      playersCount: players.length,
      shouldShowLeaderboard: showLeaderboard && players.length > 0 
    });
    
    // ALWAYS log player data when it changes (not just in leaderboard)
    if (players.length > 0) {
      console.log('🐛 PLAYER DATA DEBUG - Current players:');
      players.forEach((player, i) => {
        console.log(`  Player ${i}: ${player.name} (ID: ${player.id})`);
        console.log(`    chips: ${player.chips}`);
        console.log(`    totalBuyIns: ${player.totalBuyIns} (${typeof player.totalBuyIns})`);
        console.log(`    rebuyCount: ${player.rebuyCount} (${typeof player.rebuyCount})`);
        console.log(`    gameRebuys: ${player.gameRebuys} (${typeof player.gameRebuys})`);
        const fallbackCalc = 1000 * (1 + (player.rebuyCount || 0));
        const netScore = player.chips - (player.totalBuyIns || fallbackCalc);
        console.log(`    fallback calc: ${fallbackCalc}`);
        console.log(`    net score: ${netScore}`);
        console.log('    ---');
      });
    }
  }, [showLeaderboard, players]);

  // Session storage functions
  const saveGameSession = (roomId, playerName, gameInProgress = false) => {
    const session = {
      roomId,
      playerName,
      gameInProgress,
      timestamp: Date.now()
    };
    localStorage.setItem('pokerGameSession', JSON.stringify(session));
    setLastGameSession(session);
  };

  const getLastGameSession = () => {
    try {
      const stored = localStorage.getItem('pokerGameSession');
      if (stored) {
        const session = JSON.parse(stored);
        // Only consider sessions from the last 30 minutes
        if (Date.now() - session.timestamp < 30 * 60 * 1000) {
          return session;
        }
      }
    } catch (error) {
      console.error('Error reading game session:', error);
    }
    return null;
  };

  const clearGameSession = () => {
    localStorage.removeItem('pokerGameSession');
    setLastGameSession(null);
  };

  // Initialize and setup
  useEffect(() => {
    const lastSession = getLastGameSession();
    if (lastSession) {
      setLastGameSession(lastSession);
      if (lastSession.gameInProgress) {
        setShowReconnectModal(true);
      }
    }
  }, []);

  useEffect(() => {
    showdownResultsRef.current = showdownResults;
  }, [showdownResults]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hybridClient.current) {
        hybridClient.current.disconnect();
      }
    };
  }, []);

  // Auto-enable P2P mode when there are enough connected players
  useEffect(() => {
    const autoEnableP2P = async () => {
      // Filter for connected players only
      const connectedPlayers = players.filter(p => 
        p.isConnected !== false && (p.chips || 0) > 0
      );
      
      if (
        hybridClient.current && 
        connectedPlayers.length >= 2 && 
        !p2pGameMode && 
        roomId && 
        gameStarted && 
        !gameInProgress
      ) {
        try {
          const playerIds = connectedPlayers.map(p => p.id);
          await hybridClient.current.enableP2PGameMode(playerIds);
          setP2pGameMode(true);
          addNotification(`P2P mode auto-enabled for ${connectedPlayers.length} connected players! 🌐`, 'success');
        } catch (error) {
          console.error('Failed to auto-enable P2P mode:', error);
          addNotification('P2P auto-enable failed, will use server mode', 'warning');
        }
      }
    };

    const timer = setTimeout(autoEnableP2P, 2000);
    return () => clearTimeout(timer);
  }, [players.length, p2pGameMode, gameInProgress, gameStarted, roomId]);

  // Message handlers setup
  const setupMessageHandlers = () => {
    if (!hybridClient.current) return;
    
    // Setup P2P status monitoring
    hybridClient.current.on('connection-status-changed', (data) => {
      setP2pStatus(data);
      
      // Update main connection status based on hybrid status
      if (data.websocket === 'connected' || data.p2p === 'connected') {
        setConnectionStatus('connected');
      } else if (data.websocket === 'connecting' || data.p2p === 'connecting') {
        setConnectionStatus('connecting');
      } else {
        setConnectionStatus('disconnected');
      }
    });

    // Setup P2P game mode event handlers
    hybridClient.current.on('p2p-mode-enabled', (data) => {
      setP2pGameMode(true);
      const playerCount = data?.data?.playerIds?.length || players.length || 2;
      addNotification(`P2P mode enabled for ${playerCount} players`, 'success');
    });

    hybridClient.current.on('p2p-game-updated', (gameState) => {
      console.log('🎮 P2P game state updated:', gameState);
      setMentalPokerPhase(gameState.round);
      setGameState(gameState);
      setGameInProgress(true); // Mark game as in progress
      
      // Set hole cards for current player if available
      if (gameState.playerCards) {
        setHoleCards(gameState.playerCards);
      }
      
      if (hybridClient.current) {
        const stats = hybridClient.current.getP2PGameStats();
        setP2pGameStats(stats);
      }
    });

    // Room events
    hybridClient.current.on('room-joined', (data) => {
      // DEBUG: Log server's debug information about the join process
      if (data.data.debug) {
        console.log('🛠️ SERVER DEBUG INFO:', data.data.debug);
        console.log('  - Received client player ID:', data.data.debug.receivedClientPlayerId);
        console.log('  - Final assigned player ID:', data.data.debug.finalAssignedPlayerId);
        console.log('  - Found existing player by ID:', data.data.debug.foundExistingPlayerById);
        console.log('  - Found existing player by name:', data.data.debug.foundExistingPlayerByName);
        console.log('  - Was reconnection?', data.data.debug.wasReconnection);
        if (data.data.debug.existingPlayerInvestmentData) {
          console.log('  - Existing player investment data:', data.data.debug.existingPlayerInvestmentData);
        }
      }
      
      setMyPlayerId(data.data.playerId);
      myPlayerIdRef.current = data.data.playerId;
      setPlayers(data.data.players || []);
      setShowReconnectModal(false);
      setGameStarted(true);
      
      if (roomId && playerName) {
        saveGameSession(roomId.trim(), playerName.trim(), false);
      }
    });
    
    hybridClient.current.on('room-rejoined', (data) => {
      setMyPlayerId(data.data.playerId);
      myPlayerIdRef.current = data.data.playerId;
      setPlayers(data.data.players || []);
      setShowReconnectModal(false);
      setGameStarted(true);
      
      if (data.data.gameInProgress && data.data.gameState) {
        setGameInProgress(true);
        setGameState(data.data.gameState);
        setHoleCards(data.data.gameState.playerCards || []);
        addNotification('Game state restored! You can continue playing.', 'success');
      }
      
      if (roomId && playerName) {
        saveGameSession(roomId.trim(), playerName.trim(), data.data.gameInProgress || false);
      }
    });
    
    hybridClient.current.on('player-joined', (data) => {
      setPlayers(data.data.players || []);
    });
    
    hybridClient.current.on('player-left', (data) => {
      setPlayers(data.data.players || []);
    });
    
    hybridClient.current.on('player-disconnected', (data) => {
      setPlayers(data.data.players || []);
      const isMe = data.data.playerId === myPlayerIdRef.current;
      if (!isMe) {
        addNotification(`${data.data.playerName} disconnected but can reconnect`, 'warning');
      }
    });
    
    hybridClient.current.on('player-reconnected', (data) => {
      setPlayers(data.data.players || []);
      const isMe = data.data.playerId === myPlayerIdRef.current;
      if (!isMe) {
        addNotification(`${data.data.playerName} reconnected`, 'success');
      }
    });
    
    hybridClient.current.on('game-started', (data) => {
      setGameInProgress(true);
      setGameState(data.data.gameState);
      setHoleCards(data.data.gameState.playerCards || []);
      setPlayers(data.data.players || []);
      
      setShowdownResults(null);
      showdownResultsRef.current = null;
      
      if (roomId && playerName) {
        saveGameSession(roomId.trim(), playerName.trim(), true);
      }
    });
    
    // Game update handler
    hybridClient.current.on('game-updated', (data) => {
      // ALWAYS update players, even during showdown!
      if (data.data.players) {
        setPlayers(data.data.players);
      }
      
      // Only update game state if not in showdown
      if (!showdownResultsRef.current && data.data.gameState) {
        setGameState(data.data.gameState);
        setHoleCards(data.data.gameState?.playerCards || []);
      }
      
      if (data.data.action && data.data.action.playerId) {
        setTimeoutWarnings(prev => {
          const newWarnings = new Set(prev);
          newWarnings.delete(data.data.action.playerId);
          return newWarnings;
        });
      }
    });
    
    hybridClient.current.on('game-ended', (data) => {
      setGameInProgress(false);
      setGameState(null);
      setHoleCards([]);
      clearGameSession();
      
      const gameEndData = {
        winners: [data.data.winner],
        winningReason: data.data.reason,
        type: 'game-end'
      };
      setShowdownResults(gameEndData);
      showdownResultsRef.current = gameEndData;
    });
    
    // Showdown complete handler
    hybridClient.current.on('showdown-complete', (data) => {
      setGameInProgress(false);
      setGameState(null);
      setHoleCards([]);
      
      const showdownData = {
        winners: data.data.winners,
        winningHand: data.data.winningHand,
        pot: data.data.pot,
        allHands: data.data.allPlayerHands,
        communityCards: data.data.communityCards,
        type: 'showdown'
      };
      
      setShowdownResults(showdownData);
      showdownResultsRef.current = showdownData;
      clearGameSession();
      
      // Don't update players here - let game-updated handle it!
    });
    
    // Handle timeout warning (10 seconds before timeout)
    hybridClient.current.on('player-timeout-warning', (data) => {
      const notificationType = data.data.isYou ? 'warning' : 'info';
      addNotification(data.data.message, notificationType);
      
      // Add player to timeout warnings set to trigger blinking
      setTimeoutWarnings(prev => {
        const newWarnings = new Set(prev);
        newWarnings.add(data.data.playerId);
        return newWarnings;
      });
      
      // Remove the warning after 10 seconds (when timeout occurs)
      setTimeout(() => {
        setTimeoutWarnings(prev => {
          const newWarnings = new Set(prev);
          newWarnings.delete(data.data.playerId);
          return newWarnings;
        });
      }, 10000);
    });

    // Additional game events
    hybridClient.current.on('player-timeout', (data) => {
      const notificationType = data.data.isYou ? 'error' : 'warning';
      addNotification(data.data.message, notificationType);
      
      // Remove from timeout warnings since timeout occurred
      setTimeoutWarnings(prev => {
        const newWarnings = new Set(prev);
        newWarnings.delete(data.data.playerId);
        return newWarnings;
      });
    });

    // Handle both old 'player-restacked' and new 'player-rebought' messages
    hybridClient.current.on('player-restacked', (data) => {
      // Don't update players here - wait for players-updated message
      const isMe = data.data.playerId === myPlayerIdRef.current;
      const notificationType = isMe ? 'success' : 'info';
      addNotification(data.data.message, notificationType);
    });
    
    hybridClient.current.on('player-rebought', (data) => {
      // Don't update players here - wait for players-updated message
      const isMe = data.data.playerId === myPlayerIdRef.current;
      const notificationType = isMe ? 'success' : 'info';
      addNotification(data.data.message, notificationType);
    });
    
    // Handle players-updated message sent by server after rebuy
    hybridClient.current.on('players-updated', (data) => {
      setPlayers(data.data.players || []);
    });
    
    // Handle waiting players being activated for next hand
    hybridClient.current.on('waiting-players-activated', (data) => {
      addNotification(data.data.message, 'info');
      // Players will be updated through the game-updated or players-updated message
    });
    
    // Handle when a hand ends (not showdown, just all fold)
    hybridClient.current.on('hand-ended', (data) => {
      setGameInProgress(false);
      setGameState(null);
      setHoleCards([]);
      
      if (data.data.players) {
        setPlayers(data.data.players);
      }
      
      const winnerMessage = `${data.data.winner} wins $${data.data.pot} (${data.data.reason})`;
      addNotification(winnerMessage, 'success');
    });
  };

  // Rest of the component methods remain the same...
  // (I'll include just the essential ones for brevity)
  
  const handleJoinRoom = async () => {
    if (!roomId.trim() || !playerName.trim()) {
      setErrorMessage('Please enter both room ID and player name');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const serverUrl = 'wss://poker-server.antoine-delorme.workers.dev';
      hybridClient.current = HybridCommunicationManager.getInstance();
      
      setConnectionStatus('connecting');
      setupMessageHandlers();
      
      await hybridClient.current.connect(serverUrl, roomId.trim(), playerName.trim());
      setConnectionStatus('connected');
      
      const joinData = {
        roomId: roomId.trim(),
        playerName: playerName.trim(),
        playerId: hybridClient.current.playerId // Include the persistent player ID
      };
      console.log('🔍 CLIENT SENDING JOIN-ROOM:', joinData);
      hybridClient.current.send('join-room', joinData);
      
    } catch (error) {
      console.error('Failed to connect:', error);
      setErrorMessage('Failed to connect to server. Please try again.');
      setConnectionStatus('disconnected');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveGame = () => {
    if (hybridClient.current) {
      hybridClient.current.send('leave-room', {});
      hybridClient.current.disconnect();
    }
    setGameStarted(false);
    setRoomId('');
    setPlayerName('');
    setPlayers([]);
    setMyPlayerId('');
    myPlayerIdRef.current = '';
    setGameInProgress(false);
    setGameState(null);
    setHoleCards([]);
    setConnectionStatus('disconnected');
    setIsReconnecting(false);
    setTimeoutWarnings(new Set());
    clearGameSession();
  };

  const handlePokerAction = (action, amount = null) => {
    const isMyTurn = gameState?.currentPlayerTurn === myPlayerId;
    
    if (!gameState || !gameState.currentPlayerTurn) {
      addNotification('Game state not ready', 'error');
      return;
    }
    
    if (!isMyTurn) {
      addNotification('It\'s not your turn yet', 'error');
      return;
    }
    
    if (!hybridClient.current) {
      addNotification('Connection error', 'error');
      return;
    }

    if (p2pGameMode) {
      hybridClient.current.processP2PAction(action, amount);
    } else {
      hybridClient.current.send('poker-action', {
        action,
        amount
      });
    }
  };

  const handleRebuy = React.useCallback(() => {
    const player = players.find(p => p.id === myPlayerId);
    console.log('🔄 REBUY BUTTON CLICKED!', { 
      hybridClient: !!hybridClient.current, 
      websocketState: hybridClient.current?.websocket?.readyState,
      currentPlayer: player,
      gameRebuys: player?.gameRebuys || 0
    });
    
    if (hybridClient.current) {
      hybridClient.current.send('restack', {}); // Server still expects 'restack' message type
      addNotification('Requesting rebuy...', 'info');
      console.log('📤 Rebuy message sent to server');
    } else {
      console.error('❌ No hybrid client available');
      addNotification('Connection error - cannot rebuy', 'error');
    }
  }, [addNotification, players, myPlayerId]);

  const attemptReconnection = async () => {
    if (!roomId || !playerName) return;
    
    try {
      addNotification('Reconnecting...', 'info');
      const serverUrl = 'wss://poker-server.antoine-delorme.workers.dev';
      hybridClient.current = HybridCommunicationManager.getInstance();
      
      setupMessageHandlers();
      
      await hybridClient.current.connect(serverUrl, roomId.trim(), playerName.trim());
      setConnectionStatus('connected');
      
      const joinData = {
        roomId: roomId.trim(),
        playerName: playerName.trim(),
        playerId: hybridClient.current.playerId // Include the persistent player ID
      };
      console.log('🔍 CLIENT SENDING JOIN-ROOM:', joinData);
      hybridClient.current.send('join-room', joinData);
      
    } catch (error) {
      console.error('Reconnection failed:', error);
      addNotification('Reconnection failed. Trying again in 5 seconds...', 'error');
      setTimeout(attemptReconnection, 5000);
    }
  };

  const rejoinPreviousGame = async () => {
    if (!lastGameSession) return;
    
    setRoomId(lastGameSession.roomId);
    setPlayerName(lastGameSession.playerName);
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const serverUrl = 'wss://poker-server.antoine-delorme.workers.dev';
      hybridClient.current = HybridCommunicationManager.getInstance();
      
      setConnectionStatus('connecting');
      setupMessageHandlers();
      
      await hybridClient.current.connect(serverUrl, lastGameSession.roomId, lastGameSession.playerName);
      setConnectionStatus('connected');
      
      hybridClient.current.send('join-room', {
        roomId: lastGameSession.roomId,
        playerName: lastGameSession.playerName
      });
      
      addNotification(`Rejoining game in room ${lastGameSession.roomId}...`, 'info');
      
    } catch (error) {
      console.error('Failed to rejoin:', error);
      setErrorMessage('Failed to rejoin previous game. Please try manually.');
      setConnectionStatus('disconnected');
    } finally {
      setIsLoading(false);
      setShowReconnectModal(false);
    }
  };

  const startGame = () => {
    if (hybridClient.current) {
      hybridClient.current.send('start-game', {});
    }
  };

  const startP2PGame = async () => {
    if (hybridClient.current) {
      try {
        if (!p2pGameMode && players.length >= 2) {
          const playerIds = players.map(p => p.id);
          await hybridClient.current.enableP2PGameMode(playerIds);
          setP2pGameMode(true);
          addNotification(`P2P mode enabled for ${players.length} players!`, 'success');
        }
        
        if (p2pGameMode || players.length >= 2) {
          // Filter for connected players with chips
          const connectedPlayers = players.filter(p => 
            p.isConnected !== false && (p.chips || 0) > 0
          );
          
          console.log(`🎮 Attempting to start P2P game with ${connectedPlayers.length} connected players`);
          
          if (connectedPlayers.length >= 2) {
            await hybridClient.current.startP2PGame(connectedPlayers);
            addNotification(`Starting P2P game with ${connectedPlayers.length} players...`, 'info');
          } else {
            console.warn('⚠️ Not enough connected players for any game');
            addNotification(`Need at least 2 connected players to start (have ${connectedPlayers.length})`, 'error');
            return; // Don't start any game
          }
        }
      } catch (error) {
        console.error('Failed to start P2P game:', error);
        
        // Only fall back to server if it's a P2P technical failure, not player count issue
        if (error.message.includes('connected players')) {
          addNotification('Not enough players to start game: ' + error.message, 'error');
        } else {
          console.warn('🔄 P2P failed, falling back to server mode');
          addNotification('P2P failed, starting server game instead', 'warning');
          hybridClient.current.send('start-game', {});
        }
      }
    } else {
      addNotification('No connection available', 'error');
    }
  };

  // Calculated variables using extracted helpers
  const currentPlayer = getCurrentPlayer(players, myPlayerId);
  const canCheck = canPlayerCheck(currentPlayer, gameState);
  const callAmount = calculateCallAmount(currentPlayer, gameState);

  // Same render logic as original...
  if (gameStarted) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Notifications */}
        <NotificationsContainer 
          notifications={notifications} 
          onDismiss={removeNotification} 
        />
        
        {/* Reconnection overlay */}
        <ReconnectionOverlay isReconnecting={isReconnecting} />
        
        {/* Header */}
        <GameHeader 
          roomId={roomId}
          p2pGameMode={p2pGameMode}
          gameStarted={gameStarted}
          players={players}
          connectionStatus={connectionStatus}
          p2pStatus={p2pStatus}
          showLeaderboard={showLeaderboard}
          onToggleLeaderboard={() => {
            console.log('📊 TOGGLING LEADERBOARD:', { 
              currentState: showLeaderboard,
              willBecome: !showLeaderboard,
              playersCount: players.length 
            });
            setShowLeaderboard(!showLeaderboard);
          }}
          onLeaveGame={handleLeaveGame}
          currentPlayer={currentPlayer}
          gameState={gameState}
          onRebuy={handleRebuy}
        />

        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Players List */}
            <PlayersList 
              players={players}
              myPlayerId={myPlayerId}
              gameState={gameState}
              gameInProgress={gameInProgress}
              timeoutWarnings={timeoutWarnings}
            />

            {/* Game Area */}
            <div className="space-y-6">
              <GameTable 
                gameInProgress={gameInProgress}
                gameState={gameState}
                holeCards={holeCards}
                showdownResults={showdownResults}
                currentPlayer={currentPlayer}
                players={players}
                myPlayerId={myPlayerId}
                canCheck={canCheck}
                callAmount={callAmount}
                p2pGameMode={p2pGameMode}
                mentalPokerPhase={mentalPokerPhase}
                onPokerAction={handlePokerAction}
                onRebuy={handleRebuy}
                onDismissShowdown={() => setShowdownResults(null)}
              />
              
              {/* Desktop Start Game Controls */}
              {!gameInProgress && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <div className="border-b border-gray-100 px-6 py-4">
                    <h3 className="text-lg font-semibold text-gray-900">Game Controls</h3>
                  </div>
                  <div className="p-6 text-center">
                    {getPlayersWithChips(players).length >= 2 ? (
                      <div className="space-y-4">
                        <button
                          onClick={p2pGameMode ? startP2PGame : startGame}
                          className="w-full h-12 px-6 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all shadow-sm hover:shadow-md flex items-center justify-center space-x-2"
                        >
                          <span className="text-lg">🎮</span>
                          <span>Start Game</span>
                        </button>
                        
                        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
                          <div className={`w-1.5 h-1.5 rounded-full ${p2pGameMode ? 'bg-purple-500' : 'bg-blue-500'}`}></div>
                          <span>{p2pGameMode ? 'P2P Mode' : 'Server Mode'}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <button
                          disabled
                          className="w-full h-12 px-6 bg-gray-100 text-gray-500 rounded-xl font-semibold cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                          <span>⏳</span>
                          <span>Need {2 - getPlayersWithChips(players).length} more player(s)</span>
                        </button>
                        <p className="text-sm text-gray-500">Players need chips to start the game</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Bottom Section - Showdown Results and Leaderboard */}
          <div className="flex gap-6 justify-center">
            {/* Showdown Results Display */}
            <ShowdownResults 
              showdownResults={showdownResults}
              onDismiss={() => setShowdownResults(null)}
            />
            
            {/* Leaderboard at bottom */}
            {showLeaderboard && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm max-w-md w-full">
                {console.log('🎨 LEADERBOARD MODAL RENDERING AT BOTTOM!')}
                <div className="border-b border-gray-100 px-6 py-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🏆</span>
                      <h2 className="text-lg font-semibold text-gray-900">Leaderboard</h2>
                    </div>
                    <button 
                      onClick={() => {
                        console.log('🚪 BOTTOM LEADERBOARD CLOSE CLICKED');
                        setShowLeaderboard(false);
                      }}
                      className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {players.length > 0 ? (() => {
                      // DEBUG: Log player data to diagnose reconnection investment history bug
                      console.log('🐛 LEADERBOARD DEBUG - Player data received:');
                      players.forEach((player, i) => {
                        console.log(`  Player ${i}: ${player.name} (ID: ${player.id})`);
                        console.log(`    chips: ${player.chips}`);
                        console.log(`    totalBuyIns: ${player.totalBuyIns} (${typeof player.totalBuyIns})`);
                        console.log(`    rebuyCount: ${player.rebuyCount} (${typeof player.rebuyCount})`);
                        console.log(`    gameRebuys: ${player.gameRebuys} (${typeof player.gameRebuys})`);
                        const fallbackCalc = 1000 * (1 + (player.rebuyCount || 0));
                        const netScore = player.chips - (player.totalBuyIns || fallbackCalc);
                        console.log(`    fallback calc: ${fallbackCalc}`);
                        console.log(`    net score: ${netScore}`);
                        console.log('    ---');
                      });

                      // Calculate net scores and sort by profit/loss (descending)
                      const playersWithScores = players.map(player => ({
                        ...player,
                        // Use totalBuyIns if available, otherwise calculate from rebuyCount (persistent across reconnections)
                        netScore: player.chips - (player.totalBuyIns || (1000 * (1 + (player.rebuyCount || 0))))
                      })).sort((a, b) => b.netScore - a.netScore);

                      return playersWithScores.map((player, index) => (
                        <div key={player.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-xs ${
                              index === 0 ? 'bg-amber-100 text-amber-700' :
                              index === 1 ? 'bg-gray-100 text-gray-700' :
                              index === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{player.name}</div>
                              <div className="text-xs text-gray-500">${player.chips} chips</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`font-semibold ${
                              player.netScore >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {player.netScore >= 0 ? '+' : ''}${player.netScore}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(player.rebuyCount || 0) + 1} buy-ins
                            </div>
                          </div>
                        </div>
                      ));
                    })() : (
                      <div className="text-gray-500 text-center py-8">No players found</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show join room modal directly (no welcome screen)
  return (
    <div>
      {/* Notifications */}
      <NotificationsContainer 
        notifications={notifications} 
        onDismiss={removeNotification} 
      />

      {/* Join Room Modal - Always visible when not in a game */}
      <JoinRoomModal 
        isOpen={true}
        roomId={roomId}
        setRoomId={setRoomId}
        playerName={playerName}
        setPlayerName={setPlayerName}
        errorMessage={errorMessage}
        isLoading={isLoading}
        onJoinRoom={handleJoinRoom}
        onClose={() => {}} // No close action - user must join a room
      />
      
      {/* Reconnect Modal */}
      <ReconnectModal 
        isOpen={showReconnectModal}
        lastGameSession={lastGameSession}
        isMobile={isMobile}
        onRejoin={rejoinPreviousGame}
        onStartNew={() => setShowReconnectModal(false)}
        onCancel={() => setShowReconnectModal(false)}
      />
    </div>
  );
}

export default AppFixed;